import { Link } from "react-router-dom";
import { ChevronRight, Clock, Calendar } from "lucide-react";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Badge } from "@/components/ui/badge";
import { ArticleWithCategory } from "@/hooks/useArticles";

interface ArticleHeaderProps {
  article: ArticleWithCategory;
}

// Calculate reading time based on content (average 200 words per minute)
function calculateReadingTime(content: string): number {
  const words = content.trim().split(/\s+/).length;
  const minutes = Math.ceil(words / 200);
  return Math.max(1, minutes);
}

// Format date in Danish
function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString("da-DK", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

// Get category color
function getCategoryColor(categorySlug: string): string {
  const colorMap: Record<string, string> = {
    "sabbataar-intro": "bg-lp-blue text-white",
    "ideer-inspiration": "bg-lp-yellow text-foreground",
    "rejse-udland": "bg-lp-green text-white",
    "hoejskole-uddannelse": "bg-lp-orange text-white",
    "arbejde-frivillighed": "bg-lp-purple text-white",
    "praktisk-oekonomi": "bg-lp-red text-white",
  };
  return colorMap[categorySlug] || "bg-primary text-white";
}

export function ArticleHeader({ article }: ArticleHeaderProps) {
  const readingTime = calculateReadingTime(article.content);
  const categoryColor = article.category ? getCategoryColor(article.category.slug) : "bg-primary text-white";

  return (
    <header className="mb-10">
      {/* Breadcrumb */}
      <Breadcrumb className="mb-6">
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <Link to="/" className="hover:text-primary transition-colors">
                Forside
              </Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
          {article.category && (
            <>
              <BreadcrumbSeparator>
                <ChevronRight className="h-4 w-4" />
              </BreadcrumbSeparator>
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link
                    to={`/kategori/${article.category.slug}`}
                    className="hover:text-primary transition-colors"
                  >
                    {article.category.name}
                  </Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
            </>
          )}
          <BreadcrumbSeparator>
            <ChevronRight className="h-4 w-4" />
          </BreadcrumbSeparator>
          <BreadcrumbItem>
            <BreadcrumbPage className="line-clamp-1 max-w-[200px]">
              {article.title}
            </BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>

      {/* Category badge and meta info */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        {article.category && (
          <Link to={`/kategori/${article.category.slug}`}>
            <Badge className={`${categoryColor} hover:opacity-90 transition-opacity px-3 py-1`}>
              {article.category.name}
            </Badge>
          </Link>
        )}
        <div className="flex items-center gap-4 text-sm text-muted-foreground">
          <span className="flex items-center gap-1.5">
            <Clock className="w-4 h-4" />
            {readingTime} min læsetid
          </span>
          <span className="flex items-center gap-1.5">
            <Calendar className="w-4 h-4" />
            {formatDate(article.updated_at)}
          </span>
        </div>
      </div>

      {/* Title */}
      <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-5 leading-tight">
        {article.title}
      </h1>

      {/* Excerpt */}
      <p className="text-xl text-muted-foreground leading-relaxed">
        {article.excerpt}
      </p>
    </header>
  );
}

export default ArticleHeader;
